import "./ContactDetails.css";
import classNames from "classnames";
import { Booking, Contact, ToolCall } from "../interfaces";
import { FormattedMessage } from "react-intl";
import { useMemo } from "react";

import { parseDate } from "../utils/moment";

interface CallInfo {
  id: string;
  arguments: string;
  createdAt: string;
  message?: string;
}

type GroupedCalls = Record<string, CallInfo[]>;
interface LayoutProps {
  bookings: Booking[];
  contact: Contact;
  toolCalls: ToolCall[];
  createEscalation: Function;
  solveEscalations: Function;
  toggleBlock: Function;
}

type StringOrObject =
  | string
  | { [key: string]: StringOrObject }
  | StringOrObject[];

interface ArgumentProps {
  args: StringOrObject;
  k?: string;
}

const Argument: React.FC<ArgumentProps> = (data) => {
  const { args, k } = data;

  if (
    typeof args === "string" ||
    typeof args === "boolean" ||
    typeof args === "number"
  ) {
    return (
      <span className="badge auto-capitalize text-bg-light mb-1 mt-1 mr-1">
        {String(args)}
      </span>
    );
  }

  if (Array.isArray(args)) {
    return (
      <>
        {args.map((item, index) => (
          <Argument args={item} key={`${k}-${index}`} k={`${k}-${index}`} />
        ))}
      </>
    );
  }

  if (typeof args === "object" && args !== null) {
    return (
      <div
        className={k === undefined ? "d-flex overflow-auto pb-1" : "d-flex "}
      >
        {Object.entries(args).map(([k, v]) => (
          <div key={k} className="flex align-items-center">
            <span className="badge auto-capitalize text-bg-light mr-1">
              {k}
            </span>
            <Argument args={v} k={k}></Argument>
          </div>
        ))}
      </div>
    );
  }

  return <></>;
};

const ContactDetails: React.FC<LayoutProps> = (props) => {
  const {
    bookings,
    contact,
    createEscalation,
    solveEscalations,
    toggleBlock,
    toolCalls,
  } = props;

  const scrollToMessage = (messageId: string) => {
    const messageEl = document.getElementById(messageId);
    if (messageEl) {
      return messageEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  };

  const grouped = useMemo<GroupedCalls>(() => {
    return toolCalls
      ?.filter(item => item.role === "assistant")
      .flatMap(item =>
        (item.tool_calls || []).map(call => ({
          callId: call.id,
          name: call.function.name,
          args: call.function.arguments,
          createdAt: item.createdAt,
          message: item?.message,
        }))
      )
      .reduce<GroupedCalls>((acc, { callId, name, args, createdAt, message }) => {
        if (!acc[name]) acc[name] = [];
        acc[name].push({ id: callId, arguments: args, createdAt: String(createdAt), message });
        return acc;
      }, {});
  }, [toolCalls]);
  const callEntries = Object.entries(grouped) as [string, CallInfo[]][];

  const toolCallsDiv = (
    <>
      <h5 className="color-step-900">
        <FormattedMessage id="contactDetail.toolCalls" />
      </h5>
      <div className="contact-details-bookings flex flex-column gap-3">
        {callEntries.map(([name, calls]) => (
          <div className="flex flex-column gap-1" key={name}>
            <div className="booking-id font-14 color-step-950">
              <FormattedMessage id={`toolCalls.${name}`} />
            </div>
            {calls.map((call) => {
              let parsedArgs = null;
                try {
                  parsedArgs = JSON.parse(call.arguments);
                } catch (e) {
                  console.error("Error when parsing the tool call JSON: ", call);
                }

                return (
                  <div>
                    <div className="font-12 color-step-500 flex gap-3 align-items-center justify-content-between">
                      {parseDate(call.createdAt, "ddd Do, MMM YYYY")}
                      {call?.message && (
                        <span role="button" className="underline" onClick={() => scrollToMessage(String(call.message))}>
                          <FormattedMessage id="common.seeMessage" />
                        </span>
                      )}
                    </div>
                    {parsedArgs && <Argument args={parsedArgs} />}
                  </div>
                );
            })}
          </div>
        ))}
      </div>
    </>
  );

  const bookingsDiv = (
    <>
      <h5 className="color-step-900">
        <FormattedMessage id="contactDetail.bookings" />
      </h5>
      <div className="contact-details-bookings">
        {bookings.map((b, key) => (
          <div style={{ paddingBottom: "15px" }} key={key}>
            <div className="booking-id font-14 color-step-800">
              <div>{b.externalId}</div>
              <div>
                <span
                  className={classNames("badge auto-capitalize", {
                    "text-bg-danger": b.status === "cancelled",
                    "text-bg-success": b.status === "confirmed",
                    "text-bg-light": ["checked_out", "checked_in"].includes(
                      b.status
                    ),
                    "text-bg-dark": ![
                      "cancelled",
                      "confirmed",
                      "checked_out",
                      "checked_in",
                    ].includes(b.status),
                  })}
                >
                  {b.status.replace("_", " ")}
                </span>
              </div>
            </div>
            <div className="font-14 color-step-800">{b.locationName}</div>
            {b.rooms?.map((r, k) => (
              <div key={k} className="font-14 color-step-800">
                {r.name}
              </div>
            ))}
            <div className="font-12 color-step-500">
              {parseDate(b.from, "ddd Do, MMM YYYY")}
              <br />
              {parseDate(b.to, "ddd Do, MMM YYYY")}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="contact-details-container">
      <h5 className="color-step-900">
        <FormattedMessage id="contactDetail.title" />{" "}
      </h5>
      <div className="contact-details-profile">
        <div className="color-step-800 font-14">{contact.name}</div>
        {contact.username && (
          <div className="color-step-800 font-14">@{contact.username}</div>
        )}
        <div className="color-step-500 font-14">
          <FormattedMessage id={`integrations.${contact.type}Name`} />
        </div>
        {["wa", "voice"].includes(contact.type) ? (
          <div className="color-step-500 font-14">{contact.phoneNumber}</div>
        ) : null}
        {contact.followers ? (
          <div className="color-step-500 font-14">
            <b>{contact.followers}</b>{" "}
            <FormattedMessage id="contactDetail.followers" />
          </div>
        ) : null}
        {contact.igFmPageSource && (
          <div className="color-step-800 font-14 mt-1">
            <span>Via: {contact.igFmPageSource}</span>
          </div>
        )}
      </div>
      <div className="contact-details-profile">
        <div className="d-grid gap-2">
          <button
            onClick={() =>
              contact.escalated
                ? solveEscalations(contact.id)
                : createEscalation(contact.id)
            }
            className="btn btn-visito-link btn-xs"
          >
            {contact.escalated ? (
              <FormattedMessage id="chats.markAsClose" />
            ) : (
              <FormattedMessage id="chats.markAsOpen" />
            )}
          </button>

          <button
            onClick={() => toggleBlock(contact)}
            className="btn btn-visito-link-danger btn-xs"
          >
            <FormattedMessage
              id={
                contact.blocked ? `chats.unblockContact` : `chats.blockContact`
              }
            />
          </button>
        </div>
      </div>
      {contact.optedOut ? (
        <div className="contact-details-profile">
          <FormattedMessage id="chats.contactOptedOut" />
        </div>
      ) : null}
      {bookings.length ? bookingsDiv : null}
      {toolCalls.length ? toolCallsDiv : null}
    </div>
  );
};

export default ContactDetails;
